import {AnySchema, mixed, number, string} from 'yup';

import {isProduction} from '../constants';
import {V8N_MESSAGE__REQUIRED} from '../setup/yupV8nMessages';
import {stringify} from './debug';

export const objectOf = <T extends AnySchema>(schema: T) => ({
  name: 'objectOf',
  exclusive: false,
  message: "Object values don't match the given schema",
  test: (value: Record<string, unknown>) => {
    return Object.entries(value).every(([itemKey, itemValue]) => {
      const isValid = schema.isValidSync(itemValue);

      if (!isValid && !isProduction) {
        console.log(`objectOf, invalid value for key "${itemKey}"`);
        console.log(`object: ${stringify(value)}`);
        console.log(`key value: ${stringify(itemValue)}`);
      }

      return isValid;
    });
  },
});

export const validateData = (
  data: unknown,
  schema: AnySchema,
  dataDescription?: string,
): void => {
  if (isProduction) {
    return;
  }

  try {
    schema.validateSync(data, {
      // when true, parsing is skipped and the input is validated "as-is"
      strict: true,
      // Collect all errors instead of stopping at first failure
      abortEarly: false,
    });
  } catch (e) {
    console.log(`invalid data ${dataDescription || ''}`);
    console.error({errors: e});
    throw e;
  }
};

export const stringDefinedButCanBeEmpty = string().defined().strict(true);
export const stringOptionalNullable = string().optional().nullable();

export const localizedTextOptionalNullable = mixed()
  .nullable()
  .optional()
  .test({
    name: 'localized-text-optional-nullable',
    message: 'Expected a string or a translation object',
    test(value) {
      if (value == null || typeof value === 'string') {
        return true;
      }

      if (typeof value !== 'object' || Array.isArray(value)) {
        return false;
      }

      return Object.values(value).every(item => typeof item === 'string');
    },
  });

export const localizedTextRequired = mixed()
  .required()
  .test({
    name: 'localized-text-required',
    message: 'Expected a string or a translation object',
    test(value) {
      if (typeof value === 'string') {
        return true;
      }

      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
      }

      return Object.values(value).every(item => typeof item === 'string');
    },
  });

export const numberOptionalNullable = number().optional().nullable();
export const numberRequiredNullable = number()
  .nullable()
  .notOneOf([null], V8N_MESSAGE__REQUIRED);

export const numberTransformEmptyStringToNull = number().transform(
  (value: number, original: number | '') => (original === '' ? null : value),
);
